import React from 'react';
import { Award, Users, Target, TrendingUp, Zap, Globe, Shield } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import Newsletter from '../components/Newsletter';
import { values, whyChooseUs } from '../mockData';

const About = () => {
  const valueIcons = {
    "Precision": Target,
    "Discretion": Shield,
    "Speed": Zap,
    "Global Access": Globe,
    "Integrity": Award
  };

  return (
    <div className="min-h-screen bg-black">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-black via-[#1a1a1a] to-black"></div>
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 right-20 w-96 h-96 bg-[#C9A55A]/20 rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
          <h1 className="luxury-heading text-5xl md:text-6xl text-white mb-6">
            About <span className="text-[#C9A55A]">Syndicate</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            A Canada-based multinational advisory firm bridging markets, industries, and government needs
          </p>
        </div>
      </section>

      {/* Who We Are */}
      <section className="py-20 bg-gradient-to-b from-black to-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="max-w-4xl mx-auto">
            <h2 className="luxury-heading text-4xl md:text-5xl text-white mb-8">Who We Are</h2>
            <div className="space-y-6 text-gray-300 text-lg leading-relaxed">
              <p>
                Syndicate Consultants Inc is a Canada-based multinational advisory firm specializing in high-level procurement, luxury sourcing, advanced cybersecurity, and strategic development consulting.
              </p>
              <p>
                We operate across North America, Europe, and the GCC, bridging markets, industries, and government needs with integrated, on-demand solutions.
              </p>
              <p className="text-[#C9A55A] italic">
                Our strength comes from deep global relationships, long-standing supplier networks, and a proven history managing complex, high-value procurement across multiple sectors.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Luxury Retail Story */}
      <section className="py-20 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative rounded-lg overflow-hidden shadow-2xl">
              <img 
                src="https://customer-assets.emergentagent.com/job_consulting-synergy/artifacts/wgalyqcq_B10805E9-75AD-4341-91A5-ADC06B6D1AFC.png" 
                alt="About Us - Luxury Fashion Expertise"
                className="w-full h-auto"
              />
            </div>
            <div>
              <h2 className="luxury-heading text-4xl md:text-5xl text-white mb-6">A Decade in Luxury Retail & Fashion Procurement</h2>
              <div className="space-y-4 text-gray-300 leading-relaxed">
                <p>
                  Before expanding into international consulting and procurement, our leadership spent more than <span className="text-[#C9A55A] font-semibold">10 years</span> sourcing luxury fashion directly from Italy and Paris, securing wholesale collections and private-buy access to elite European brands.
                </p>
                <p>
                  Syndicate Consultants operated <span className="text-[#C9A55A] font-semibold">five luxury retail stores</span> across major Vancouver malls, giving us unmatched experience in European fashion house buying cycles, seasonal procurement, retail operations, and logistics.
                </p>
                <p className="text-gray-400 italic">
                  Today, this foundation enables our Luxury Procurement Division to deliver high-volume sourcing solutions for boutiques, private clients, and government gifting programs across the GCC — with reliability, discretion, and cultural alignment.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Expertise Areas */}
      <section className="py-20 bg-gradient-to-b from-[#0a0a0a] to-black">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="luxury-heading text-4xl md:text-5xl text-white mb-4">Unmatched Experience</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card className="bg-white/5 border-white/10 hover:border-[#C9A55A]/30 smooth-transition">
              <CardContent className="p-8">
                <div className="text-[#C9A55A] mb-4">✓</div>
                <h3 className="text-lg font-semibold text-white mb-2">European Fashion House Buying Cycles</h3>
              </CardContent>
            </Card>
            <Card className="bg-white/5 border-white/10 hover:border-[#C9A55A]/30 smooth-transition">
              <CardContent className="p-8">
                <div className="text-[#C9A55A] mb-4">✓</div>
                <h3 className="text-lg font-semibold text-white mb-2">Seasonal and Limited-Release Procurement</h3>
              </CardContent>
            </Card>
            <Card className="bg-white/5 border-white/10 hover:border-[#C9A55A]/30 smooth-transition">
              <CardContent className="p-8">
                <div className="text-[#C9A55A] mb-4">✓</div>
                <h3 className="text-lg font-semibold text-white mb-2">Retail Operations & Luxury Brand Standards</h3>
              </CardContent>
            </Card>
            <Card className="bg-white/5 border-white/10 hover:border-[#C9A55A]/30 smooth-transition">
              <CardContent className="p-8">
                <div className="text-[#C9A55A] mb-4">✓</div>
                <h3 className="text-lg font-semibold text-white mb-2">Logistics, Customs, and Import Governance</h3>
              </CardContent>
            </Card>
            <Card className="bg-white/5 border-white/10 hover:border-[#C9A55A]/30 smooth-transition">
              <CardContent className="p-8">
                <div className="text-[#C9A55A] mb-4">✓</div>
                <h3 className="text-lg font-semibold text-white mb-2">Boutique Stocking Strategies</h3>
              </CardContent>
            </Card>
            <Card className="bg-white/5 border-white/10 hover:border-[#C9A55A]/30 smooth-transition">
              <CardContent className="p-8">
                <div className="text-[#C9A55A] mb-4">✓</div>
                <h3 className="text-lg font-semibold text-white mb-2">VIP Wardrobe Curation</h3>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="luxury-heading text-4xl md:text-5xl text-white mb-4">Our Values</h2>
            <p className="text-xl text-gray-400">The principles that guide our work</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
            {values.map((value, index) => (
              <Card key={index} className="bg-white/5 border-white/10 hover:border-[#C9A55A]/50 transition-all duration-300">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-[#C9A55A]/10 rounded-full flex items-center justify-center mx-auto mb-6 border border-[#C9A55A]/20">
                    <span className="text-[#C9A55A] text-2xl font-bold">{value.title.charAt(0)}</span>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">{value.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gradient-to-b from-black to-[#0a0a0a]">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12">
          <h2 className="luxury-heading text-4xl md:text-5xl text-white mb-12 text-center">Why You Should Choose Syndicate</h2>
          <div className="space-y-4">
            {whyChooseUs.map((reason, index) => (
              <div key={index} className="flex items-start space-x-4 bg-white/5 border border-white/10 p-6 rounded-lg hover:border-[#C9A55A]/30 smooth-transition">
                <div className="w-8 h-8 bg-[#C9A55A]/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-[#C9A55A] font-bold text-sm">✓</span>
                </div>
                <p className="text-gray-300 text-lg leading-relaxed">{reason}</p>
              </div>
            ))}
          </div>
          <p className="text-center text-2xl text-[#C9A55A] mt-12 italic leading-relaxed">
            Syndicate is built for nations and leaders who demand results — not excuses.
          </p>
        </div>
      </section>

      <Newsletter />
    </div>
  );
};

export default About;